import React from 'react';
import { Pagination, Select, Space, Typography } from 'antd';
import type { PaginationProps } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;
const { Option } = Select;

export interface OrderPaginationProps {
  /** 总条数 */
  total: number;
  /** 当前页码 */
  current: number;
  /** 每页显示数量 */
  pageSize: number;
  /** 每页显示数量选项 */
  pageSizeOptions?: number[];
  /** 是否显示总数信息 */
  showTotal?: boolean;
  /** 是否显示每页数量选择器 */
  showSizeChanger?: boolean;
  /** 是否显示快速跳转 */
  showQuickJumper?: boolean;
  /** 是否显示简单分页 */
  simple?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 分页变化回调 */
  onChange?: (page: number, pageSize: number) => void;
  /** 每页显示数量变化回调 */
  onShowSizeChange?: (current: number, size: number) => void;
  /** 样式 */
  style?: React.CSSProperties;
  /** 类名 */
  className?: string;
}

const OrderPagination: React.FC<OrderPaginationProps> = ({
  total,
  current,
  pageSize,
  pageSizeOptions = [10, 20, 50, 100],
  showTotal = true,
  showSizeChanger = true,
  showQuickJumper = true,
  simple = false,
  disabled = false,
  onChange,
  onShowSizeChange,
  style,
  className
}) => {
  const { t } = useTranslation(['modules', 'common']);

  // 计算总页数
  const totalPages = Math.ceil(total / pageSize);
  // 计算当前页的开始和结束索引
  const startIndex = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const endIndex = Math.min(current * pageSize, total);

  // 处理页码变化
  const handleChange = (page: number, size: number) => {
    if (pageSize === size) {
      // 只有页码变化
      onChange?.(page, size);
    } else {
      // 每页数量变化，回到第一页
      onShowSizeChange?.(page, size);
    }
  };

  // 自定义显示总数
  const showTotalText = (total: number, range: [number, number]) => {
    return (
      <span>
        {t('modules:order.query.pagination.showTotal', {
          total,
          start: range[0],
          end: range[1]
        })}
      </span>
    );
  };

  // 自定义渲染分页项
  const itemRender: PaginationProps['itemRender'] = (
    page,
    type,
    originalElement
  ) => {
    if (type === 'prev') {
      return (
        <a
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 32,
            height: 32,
            padding: '0 8px'
          }}
        >
          {t('common:previous', '上一页')}
        </a>
      );
    }
    if (type === 'next') {
      return (
        <a
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 32,
            height: 32,
            padding: '0 8px'
          }}
        >
          {t('common:next', '下一页')}
        </a>
      );
    }
    if (type === 'jump-prev' || type === 'jump-next') {
      return <span>...</span>;
    }
    return originalElement;
  };

  // 如果总数为0，不显示分页
  if (total === 0) {
    return null;
  }

  return (
    <div
      className={`order-pagination ${className || ''}`}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 0',
        ...style
      }}
    >
      {/* 左侧信息 */}
      <div style={{ flex: 1 }}>
        {showTotal && (
          <Space>
            <Text type="secondary">
              {t('modules:order.query.pagination.total', '共')} {total} {t('modules:order.query.pagination.items', '条')}
            </Text>
            <Text type="secondary">
              {t('modules:order.query.pagination.current', '当前显示')} {startIndex}-{endIndex}
            </Text>
            <Text type="secondary">
              {t('modules:order.query.pagination.page', '第')} {current} / {totalPages} {t('modules:order.query.pagination.page', '页')}
            </Text>
          </Space>
        )}
      </div>

      {/* 中间分页控件 */}
      <div style={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          current={current}
          pageSize={pageSize}
          total={total}
          onChange={handleChange}
          onShowSizeChange={onShowSizeChange}
          showTotal={showTotal ? showTotalText : undefined}
          showSizeChanger={showSizeChanger}
          showQuickJumper={showQuickJumper}
          simple={simple}
          disabled={disabled}
          pageSizeOptions={pageSizeOptions}
          itemRender={itemRender}
          size="default"
        />
      </div>

      {/* 右侧每页数量选择器 */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
        {showSizeChanger && (
          <Space>
            <Text type="secondary">
              {t('modules:order.query.pagination.pageSize', '每页显示')}:
            </Text>
            <Select
              value={pageSize}
              onChange={(value) => {
                onShowSizeChange?.(current, value);
              }}
              size="small"
              style={{ width: 80 }}
              disabled={disabled}
            >
              {pageSizeOptions.map(size => (
                <Option key={size} value={size}>
                  {size} {t('modules:order.query.pagination.items', '条')}
                </Option>
              ))}
            </Select>
          </Space>
        )}

        {/* 快速跳转 */}
        {showQuickJumper && (
          <Space style={{ marginLeft: 16 }}>
            <Text type="secondary">
              {t('modules:order.query.pagination.jumpTo', '跳至')}:
            </Text>
            <Select
              value={current}
              onChange={(value) => {
                onChange?.(value, pageSize);
              }}
              size="small"
              style={{ width: 80 }}
              disabled={disabled}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <Option key={page} value={page}>
                  {t('modules:order.query.pagination.page', '第')} {page} {t('modules:order.query.pagination.page', '页')}
                </Option>
              ))}
            </Select>
          </Space>
        )}
      </div>
    </div>
  );
};

export default OrderPagination;